import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'

import Icon from '../../../ui/Icon'
import { Buttons } from '../../../buttons/Buttons'
import { Button } from '../../../buttons/Button'

import { NextSessionWidget } from '../../../../types/widget/widget'
import {
  displayLocalDateTimeHourMinute,
  displayWeekdayMonthDay,
  getCountdown,
  toWeek,
} from '../../../../utils/dateUtils'

type NextSessionProps = {
  nextSession: NextSessionWidget | null
}

export const NextSession = ({ nextSession }: NextSessionProps) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('dashboard')
  const [countdown, setCountdown] = useState(getCountdown())
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    if (!nextSession?.bookingStartTime) {
      return
    }

    const update = () => {
      setNow(Date.now())
      setCountdown(getCountdown(nextSession.bookingStartTime))
    }

    update()
    const intervalId = setInterval(update, 1000)
    return () => clearInterval(intervalId)
  }, [nextSession?.bookingStartTime])

  if (!nextSession) {
    return (
      <div className="ph-row ph-between next-play">
        <div className="next-session-info">
          <div className="eyebrow mb-8">
            {t('dashboard.next-session.no-session-eyebrow')}
          </div>
          <h1 className="display next-session-empty-title">
            {t('dashboard.next-session.no-session')}
          </h1>
          <p className="muted mt-8 next-session-details">
            {t('dashboard.next-session.no-session-description')}
          </p>
        </div>
        <Buttons className="next-session-actions">
          <Button
            icon={<Icon name="plus" size={14} />}
            variant="primary"
            label={t('dashboard.next-session.book-court')}
            onClick={() => navigate('/booking')}
          />
        </Buttons>
      </div>
    )
  }

  const bookingStart = new Date(nextSession.bookingStartTime).getTime()
  const bookingEnd = nextSession.bookingEndTime
    ? new Date(nextSession.bookingEndTime).getTime()
    : Number.NaN
  const inProgress =
    Number.isFinite(bookingStart) &&
    now >= bookingStart &&
    (!Number.isFinite(bookingEnd) || now < bookingEnd)

  return (
    <div className="ph-row ph-between next-play">
      <div className="next-session-info">
        <div className="eyebrow mb-8">
          {t('dashboard.next-session.eyebrow', {
            day: displayWeekdayMonthDay(
              nextSession.bookingStartTime,
              i18n.language
            ),
            week: toWeek(nextSession.bookingStartTime),
          })}
        </div>
        {inProgress ? (
          <>
            <span className="badge badge-success next-session-live-status">
              <span className="next-session-live-dot" />
              {t('dashboard.next-session.live')}
            </span>
            <h1 className="display next-session-live-title">
              {t('dashboard.next-session.in-progress')}
            </h1>
            <p className="muted mt-8 next-session-details">
              {nextSession.bookingEndTime
                ? t('dashboard.next-session.in-progress-description', {
                    name: nextSession.courtName,
                    hour: displayLocalDateTimeHourMinute(
                      nextSession.bookingEndTime
                    ),
                  })
                : t(
                    'dashboard.next-session.in-progress-description-without-end',
                    { name: nextSession.courtName }
                  )}
            </p>
          </>
        ) : (
          <>
            <h1 className="display next-session-countdown">
              {countdown.days > 0 && (
                <>
                  {countdown.days}
                  <span className="next-session-unit">
                    {t('dashboard.next-session.unit.days', { count: countdown.days })}
                  </span>
                </>
              )}
              {String(countdown.hours).padStart(2, '0')}
              <span className="next-session-unit">
                {t('dashboard.next-session.unit.hours', { count: countdown.hours })}
              </span>
              {String(countdown.minutes).padStart(2, '0')}
              <span className="next-session-unit">
                {t('dashboard.next-session.unit.minutes', { count: countdown.minutes })}
              </span>
            </h1>
            <p className="muted mt-8 next-session-details">
              <Trans
                ns="dashboard"
                i18nKey="dashboard.next-session.details"
                values={{
                  name: nextSession.courtName,
                  hour: displayLocalDateTimeHourMinute(
                    nextSession.bookingStartTime
                  ),
                }}
                components={{
                  session: <strong className="next-session-court" />,
                }}
              />
            </p>
          </>
        )}
      </div>
      <Buttons className="next-session-actions">
        <Button
          icon={<Icon name="plus" size={14} />}
          variant="primary"
          label={t('dashboard.next-session.book-court')}
          onClick={() => navigate('/booking')}
        />
      </Buttons>
    </div>
  )
}
